import React, { useEffect, useMemo, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { Link } from "react-router-dom";
import apiService from "../../actions/apiService";
import { useAuth } from "../../context/authContext";
import ConfirmTransactionModal from "../../components/models/ConfirmTransactionModal";

// Utility function to truncate text
const truncateText = (text, maxLength = 25) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const STATUS_PILL = {
  pending: "bg-[#FFC49F] text-[#D35100]",
  accepted: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  buyer_confirmed: "bg-[#FFC49F] text-[#D35100]",
  confirmed: "bg-[#B9FFCC] text-[#6C8F75]",
  released: "bg-[#B9FFCC] text-[#6C8F75]",
  disputed: "bg-red-100 text-red-700",
  resolved: "bg-blue-100 text-blue-700",
  cancelled: "bg-[#FF5040] text-[#ffffff]",
  payment_received: "bg-[#E0E0E0] text-[#575757]",
};

// Helper function to get status display text and pill key for buyers
const getStatusDisplay = (
  status,
  adminVerificationStatus,
  paymentCancelledReason,
  paymentInitiatedAt,
  sellerConfirmed = false
) => {
  if (status === "cancelled") {
    if (paymentCancelledReason === "item_not_authentication") {
      return {
        text: "Cancelled",
        pillKey: "cancelled",
      };
    }
    return { text: "Cancelled", pillKey: "cancelled" };
  }
  // If payment was initiated (buyer clicked "I made the transfer"), show "Verification in progress"
  if (status === "pending" && paymentInitiatedAt) {
    return {
      text: "Verification in progress",
      smallText: "An admin is checking the payment",
      pillKey: "payment_received",
    };
  }
  if (status === "pending") {
    return {
      text: "pending payment",
      smallText: "Awaiting escrow payment",
      pillKey: "pending",
    };
  }
  // If seller confirmed first, show funds on hold (buyer still needs to confirm)
  if (sellerConfirmed && status !== "confirmed" && status !== "released") {
    return {
      text: "funds on hold",
      smallText: "Awaiting your confirmation",
      pillKey: "buyer_confirmed",
    };
  }
  // After admin accepts payment (status="accepted" and admin_verification_status="verified"), show "Funds on hold"
  if (status === "accepted" && adminVerificationStatus === "verified") {
    return {
      text: "funds on hold",
      smallText: "Awaiting transaction confirmation",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "buyer_confirmed") {
    return {
      text: "funds on hold",
      smallText: "Awaiting transaction confirmation",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "confirmed") {
    return {
      text: "completed",
      pillKey: "released",
      smallText: "",
    };
  }
  if (adminVerificationStatus === "released" || "Released") {
    return { text: "completed", pillKey: "released" };
  }
  return {
    text: status.charAt(0).toUpperCase() + status.slice(1).replace("_", " "),
    pillKey: status,
  };
};

// Map backend transaction to UI row
const mapApiTransactionToRow = (t) => {
  if (!t || typeof t !== "object") return null;
  const listing = t.item || t.listing || {};
  // const buyer = t.buyer || t.user || {};
  const seller = t.seller || {};
  let status =
    t.status ||
    t.payment_status ||
    t.transfer_status ||
    t.admin_verification_status;
  status = (status ? String(status) : "pending").toLowerCase();
  // Status mapping is now direct - no transformation needed
  const amountRaw =
    t.buyer_paid_amount ?? listing.price ?? t.amount ?? t.total_amount ?? null;
  const amountStr =
    amountRaw == null
      ? "—"
      : typeof amountRaw === "number"
      ? `${amountRaw.toLocaleString()}€`
      : `${amountRaw}€`;
  const imageBase = process.env.REACT_APP_IMAGE_URL || "";
  return {
    id: t.id || t.transaction_id || t.reference || "—",
    listing: {
      title: listing.title || listing.name || "—",
      type: listing.custom_category
        ? `${listing.custom_category}`
        : listing.category || "—",
      thumb:
        (Array.isArray(listing.images) && listing.images[0]?.file_url
          ? imageBase + listing.images[0]?.file_url
          : null) ||
        listing.thumbnail_url ||
        listing.image_url ||
        "/logo192.png",
    },
    buyer: {
      name:
        [
          seller.firstName || seller.first_name,
          seller.lastName || seller.last_name,
        ]
          .filter(Boolean)
          .join(" ") ||
        seller.name ||
        seller.email ||
        "—",
      email: seller.email || "—",
      avatar: seller.profile_image_url || seller.avatar_url || "/logo192.png",
    },
    amount: amountStr,
    status,
    buyerConfirmed: Boolean(t.buyer_confirmed),
    sellerConfirmed: Boolean(t.seller_confirmed),
    adminVerificationStatus: t.admin_verification_status
      ? String(t.admin_verification_status).toLowerCase()
      : "",
    paymentCancelledReason: t.payment_cancelled_reason || null,
    paymentInitiatedAt: t.payment_initiated_at || null,
    itemId: listing.id,
  };
};

const BuyerTransactions = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsApi, setRowsApi] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      try {
        const params = {
          buyer_id: user.id,
          search: search?.trim() || null,
          status: activeTab !== "all" ? activeTab : null,
          page: 1,
          limit: 100,
        };
        const res = await apiService.get("/transactions/by-user/list", params);
        if (!active) return;
        if (res?.success) {
          const list = res?.data?.transactions;
          const normalized = (Array.isArray(list) ? list : [])
            .map(mapApiTransactionToRow)
            .filter(Boolean);
          setRowsApi(normalized);
        } else {
          setError(res?.error || "Failed to fetch transactions");
          setRowsApi([]);
        }
      } catch (e) {
        if (active) {
          setError("Network error. Please try again.");
          setRowsApi([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [user?.id, search, activeTab]);

  // Function to open confirmation modal
  const handleOpenConfirmModal = (transaction) => {
    setSelectedTransaction(transaction);
    setModalError(null);
    setModalOpen(true);
  };

  // Function to confirm seller
  const handleConfirmSeller = async () => {
    if (!selectedTransaction?.id) return;

    setConfirmingId(selectedTransaction.id);
    try {
      const response = await apiService.post(
        `/transactions/${selectedTransaction.id}/confirm-buyer`
      );
      if (response?.success) {
        // Refresh the transactions list
        const params = {
          buyer_id: user.id,
          search: search?.trim() || null,
          status: activeTab !== "all" ? activeTab : null,
          page: 1,
          limit: 100,
        };
        const res = await apiService.get("/transactions/by-user/list", params);
        if (res?.success) {
          const list = res?.data?.transactions;
          const normalized = (Array.isArray(list) ? list : [])
            .map(mapApiTransactionToRow)
            .filter(Boolean);
          setRowsApi(normalized);
        }
        setModalOpen(false);
        setSelectedTransaction(null);
      } else {
        setModalError(response?.error || "Failed to confirm seller");
      }
    } catch (err) {
      setModalError("Network error. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  // Function to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
    setModalError(null);
  };

  const rows = useMemo(() => {
    let r = [...rowsApi];
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (x) =>
          String(x.id).toLowerCase().includes(q) ||
          String(x.listing.title).toLowerCase().includes(q) ||
          String(x.buyer.name).toLowerCase().includes(q)
      );
    }
    return r;
  }, [rowsApi, search]);

  console.log(rows);

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              My transactions
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Track your purchases and transaction history
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-8">
          <div className="w-full bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] p-5 pt-[40px] md:pt-5 grid md:flex items-center gap-4">
            <div className="relative flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 rounded-[8px] border border-gray-200 pl-10 pr-[12px] text-caption bg-white"
                placeholder="Search transaction..."
              />
              <div className="absolute left-3 top-2.5 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <g opacity="0.25">
                    <path
                      d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M3.88589 5.66687C3.12955 4.82155 2.75138 4.39889 2.73712 4.03968C2.72473 3.72764 2.85882 3.42772 3.09963 3.22889C3.37684 3 3.94399 3 5.07828 3H19.9212C21.0555 3 21.6227 3 21.8999 3.22889C22.1407 3.42772 22.2748 3.72764 22.2624 4.03968C22.2481 4.39889 21.8699 4.82155 21.1136 5.66687L15.4074 12.0444C15.2566 12.2129 15.1812 12.2972 15.1275 12.3931C15.0798 12.4781 15.0448 12.5697 15.0236 12.6648C14.9997 12.7721 14.9997 12.8852 14.9997 13.1113V18.4584C14.9997 18.6539 14.9997 18.7517 14.9682 18.8363C14.9403 18.911 14.895 18.9779 14.836 19.0315C14.7692 19.0922 14.6784 19.1285 14.4969 19.2012L11.0969 20.5612C10.7293 20.7082 10.5455 20.7817 10.398 20.751C10.269 20.7242 10.1558 20.6476 10.083 20.5377C9.99975 20.4122 9.99975 20.2142 9.99975 19.8184V13.1113C9.99975 12.8852 9.99975 12.7721 9.97587 12.6648C9.95469 12.5697 9.91971 12.4781 9.87204 12.3931C9.81828 12.2972 9.7429 12.2129 9.59213 12.0444L3.88589 5.66687Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="all">All status</option>
                <option value="pending">Pending</option>
                <option value="released">Funds Released</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="18"
                  viewBox="0 0 21 18"
                  fill="none"
                >
                  <path
                    d="M15.5 1V17M15.5 17L11.5 13M15.5 17L19.5 13M5.5 17V1M5.5 1L1.5 5M5.5 1L9.5 5"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="recent">Most recent</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-4 px-5 bg-[#F5F8FF] md:bg-white">
        {loading && <div className="text-sm text-gray-500 py-4">Loading…</div>}
        {!!error && <div className="text-sm text-red-600 py-4">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="text-sm text-center text-gray-500 py-4">
            No transactions found.
          </div>
        )}
        {rows.map((row) => (
          <div
            key={row.id}
            className="relative bg-white border border-[#DCEDFF] rounded-[12px] p-4 shadow-sm"
          >
            {/* status pill */}
            {(() => {
              const statusDisplay = getStatusDisplay(
                row.status,
                row.adminVerificationStatus,
                row.paymentCancelledReason,
                row.paymentInitiatedAt,
                row.sellerConfirmed
              );
              return (
                <>
                  <span
                    className={`absolute top-3 right-3 text-[12px] font-semibold px-2.5 py-1 rounded-full capitalize ${
                      STATUS_PILL[statusDisplay.pillKey] ||
                      STATUS_PILL[row.status] ||
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {statusDisplay.text}
                  </span>
                  {statusDisplay.smallText && (
                    <span className="text-[12px] text-[#1E1E1E] opacity-50">
                      {statusDisplay.smallText}
                    </span>
                  )}
                </>
              );
            })()}

            {/* title */}
            <div className="text-[14px] font-semibold text-[#1E1E1E] pr-24">
              {truncateText(row.listing.title, 16)}
            </div>
            <div className="text-[12px] text-[#1E1E1E] opacity-50">
              {row.listing.type}
            </div>
            <div className="text-[12px] text-[#1E1E1E] opacity-50 mt-1">
              {row.id}
            </div>

            {/* seller */}
            <div className="flex items-center gap-2 mt-3">
              {row.buyer.avatar ? (
                <img
                  src={row.buyer.avatar}
                  alt="avatar"
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-6 h-6 text-xl font-semibold flex items-center justify-center">
                  {row.buyer.name?.charAt(0)}
                </span>
              )}
              <div>
                <div className="text-[12px] text-[#1E1E1E] opacity-75">
                  {row.buyer.name}
                </div>
                <div className="text-[10px] text-[#1E1E1E] opacity-50">
                  {row.buyer.email}
                </div>
              </div>
            </div>

            {/* amount */}
            <div className="mt-3 text-[18px] font-bold text-[#000025]">
              {row.amount}
            </div>

            {/* actions */}
            <div className="mt-3 flex items-center gap-3">
              <Link
                to={`/buyer/listings/${row.itemId || ""}`}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-200 text-[#1E1E1E] text-[14px] py-2.5 px-3 rounded-[8px] bg-white"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
                View
              </Link>
              {(() => {
                const statusDisplay = getStatusDisplay(
                  row.status,
                  row.adminVerificationStatus,
                  row.paymentCancelledReason,
                  row.paymentInitiatedAt,
                  row.sellerConfirmed
                );
                // Only mark as completed if actually released/confirmed, not when only seller confirmed
                const isCompleted =
                  (statusDisplay.text === "completed" ||
                    statusDisplay.pillKey === "released" ||
                    row.status === "released" ||
                    row.adminVerificationStatus === "released") &&
                  !(
                    row.sellerConfirmed &&
                    !row.buyerConfirmed &&
                    row.status !== "confirmed" &&
                    row.status !== "released"
                  );

                if (isCompleted) {
                  return (
                    <Link
                      to={`/buyer/transactions/${row.id}/invoice`}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-[#2A8BF7] text-[#2A8BF7] text-[14px] py-2.5 px-3 rounded-[8px] bg-white"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      See receipt
                    </Link>
                  );
                }
                if (
                  !isCompleted &&
                  !row.buyerConfirmed &&
                  row.status !== "cancelled" &&
                  (["accepted", "confirmed", "buyer_confirmed"].includes(
                    row.status
                  ) ||
                    row.sellerConfirmed)
                ) {
                  return (
                    <button
                      onClick={() => handleOpenConfirmModal(row)}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B9FFCC] text-[#16653F] text-[14px] py-2.5 px-3 rounded-[8px]"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="13"
                        viewBox="0 0 18 13"
                        fill="none"
                      >
                        <path
                          d="M17 1L6 12L1 7"
                          stroke="#16653F"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Confirm
                    </button>
                  );
                }
                return null;
              })()}
              <button
                className="h-10 w-10 flex items-center justify-center rounded-[8px] border border-gray-200 bg-white"
                title="More"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M2 17V16.8498C2 16.5333 2 16.3751 2.02421 16.2209C2.0457 16.084 2.08136 15.9497 2.13061 15.8202C2.18609 15.6743 2.2646 15.5369 2.42162 15.2622L6 9M2 17C2 19.2091 3.79086 21 6 21C8.20914 21 10 19.2091 10 17M2 17V16.8C2 16.52 2 16.38 2.0545 16.273C2.10243 16.1789 2.17892 16.1024 2.273 16.0545C2.37996 16 2.51997 16 2.8 16H9.2C9.48003 16 9.62004 16 9.727 16.0545C9.82108 16.1024 9.89757 16.1789 9.9455 16.273C10 16.38 10 16.52 10 16.8V17M6 9L9.57838 15.2622C9.7354 15.5369 9.81391 15.6743 9.86939 15.8202C9.91864 15.9497 9.9543 16.084 9.97579 16.2209C10 16.3751 10 16.5333 10 16.8498V17M6 9L18 7M14 15V14.8498C14 14.5333 14 14.3751 14.0242 14.2209C14.0457 14.084 14.0814 13.9497 14.1306 13.8202C14.1861 13.6743 14.2646 13.5369 14.4216 13.2622L18 7M14 15C14 17.2091 15.7909 19 18 19C20.2091 19 22 17.2091 22 15M14 15V14.8C14 14.52 14 14.38 14.0545 14.273C14.1024 14.1789 14.1789 14.1024 14.273 14.0545C14.38 14 14.52 14 14.8 14H21.2C21.48 14 21.62 14 21.727 14.0545C21.8211 14.1024 21.8976 14.1789 21.9455 14.273C22 14.38 22 14.52 22 14.8V15M18 7L21.5784 13.2622C21.7354 13.5369 21.8139 13.6743 21.8694 13.8202C21.9186 13.9497 21.9543 14.084 21.9758 14.2209C22 14.3751 22 14.5333 22 14.8498V15M12 3V8"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block mt-8 space-y-8 px-5 md:px-0">
        {/* Requests table */}
        <section className="rounded-[12px] bg-[#F5F8FF]">
          <div className="bg-white border rounded-[12px] border-[#DCEDFF] overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-[97%] ms-5 mr-5 mx-auto mt-5 mb-2 min-w-[900px]">
                <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
                  <tr>
                    <th className="text-left min-w-[150px] py-3 px-5 rounded-tl-[12px]">
                      Transaction ID
                    </th>
                    <th className="text-left min-w-[250px] py-3 px-5">
                      Listing
                    </th>
                    <th className="text-left min-w-[200px] py-3 px-5">
                      Seller
                    </th>
                    <th className="text-left min-w-[150px] py-3 px-5">
                      Amount
                    </th>
                    <th className="text-left min-w-[220px] py-3 px-5">
                      Status
                    </th>
                    <th className="text-left py-3 px-5 rounded-tr-[12px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td
                        className="py-6 text-center w-full px-5 text-sm text-gray-500"
                        colSpan={6}
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!!error && !loading && (
                    <tr>
                      <td
                        className="py-6 px-5 text-sm text-red-600"
                        colSpan={6}
                      >
                        {error}
                      </td>
                    </tr>
                  )}
                  {!loading && !error && rows.length === 0 && (
                    <tr>
                      <td
                        className="py-6 text-center px-5 text-sm text-gray-500"
                        colSpan={6}
                      >
                        No transactions found.
                      </td>
                    </tr>
                  )}
                  {rows.map((row, idx) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-4">
                          <span className="text-[#000025] font-[400] text-[16px] opacity-50">
                            {idx + 1}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-4">
                          <div className="max-h-[48px] max-w-[48px] bg-gray-100 rounded overflow-hidden">
                            <img
                              src={row.listing.thumb}
                              alt="thumb"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="text-product-title text-esdarkGray line-clamp-1">
                              {truncateText(row.listing.title)}
                            </div>
                            <div className="text-[14px] opacity-75 text-[#000025]">
                              {row.listing.type}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-4">
                          {row.buyer.avatar ? (
                            <img
                              src={row.buyer.avatar}
                              alt="avatar"
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-8 h-8 text-xl font-semibold flex items-center justify-center">
                              {row.buyer.name?.charAt(0)}
                            </span>
                          )}
                          <div>
                            <div className="text-esdarkGray font-[600] opacity-75 text-nav-d">
                              {row.buyer.name}
                            </div>
                            <div className="text-[12px] font-[500] opacity-50 text-esdarkGray">
                              {row.buyer.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5 text-[#000025] text-page-subtitle-d">
                        {row.amount}
                      </td>
                      <td className="py-3 px-5">
                        {(() => {
                          const statusDisplay = getStatusDisplay(
                            row.status,
                            row.adminVerificationStatus,
                            row.paymentCancelledReason,
                            row.paymentInitiatedAt,
                            row.sellerConfirmed
                          );
                          return (
                            <>
                              <span
                                className={`text-[14px] justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full ${
                                  STATUS_PILL[statusDisplay.pillKey] ||
                                  STATUS_PILL[row.status] ||
                                  "bg-[#E0E0E0] text-[#575757]"
                                }`}
                              >
                                {statusDisplay.text}
                              </span>
                              {statusDisplay.smallText && (
                                <span className="text-[12px] text-[#1E1E1E] opacity-50">
                                  {statusDisplay.smallText}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-4">
                          <Link
                            to={`/buyer/transactions/${
                              row.itemId || ""
                            }/details`}
                            title="Message"
                            className="h-10 w-fit px-3 flex items-center justify-center border border-gray-200 gap-4 rounded-[8px] bg-[#FFFFFF] text-[#1E1E1E] border:opacity-50"
                          >
                            <span className="text-[14px] font-[600]">View</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <g opacity="0.5">
                                <path
                                  d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                                  stroke="#1E1E1E"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                                  stroke="#1E1E1E"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                            </svg>
                          </Link>
                          <div className="flex justify-center items-center gap-4">
                            {(() => {
                              const statusDisplay = getStatusDisplay(
                                row.status,
                                row.adminVerificationStatus,
                                row.paymentCancelledReason,
                                row.paymentInitiatedAt,
                                row.sellerConfirmed
                              );
                              // Only mark as completed if actually released/confirmed, not when only seller confirmed
                              const isCompleted =
                                (statusDisplay.text === "completed" ||
                                  statusDisplay.pillKey === "released" ||
                                  row.status === "released" ||
                                  row.adminVerificationStatus === "released") &&
                                !(
                                  row.sellerConfirmed &&
                                  !row.buyerConfirmed &&
                                  row.status !== "confirmed" &&
                                  row.status !== "released"
                                );

                              if (isCompleted) {
                                return (
                                  <Link
                                    to={`/buyer/transactions/${row.id}/invoice`}
                                    title="See receipt"
                                    className="flex h-10 px-3.5 gap-4 items-center justify-center rounded-[8px] transition-colors border border-[#2A8BF7] text-[#2A8BF7] text-[14px] font-[600] bg-white"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                      <polyline points="14 2 14 8 20 8"></polyline>
                                      <line
                                        x1="16"
                                        y1="13"
                                        x2="8"
                                        y2="13"
                                      ></line>
                                      <line
                                        x1="16"
                                        y1="17"
                                        x2="8"
                                        y2="17"
                                      ></line>
                                      <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    See receipt
                                  </Link>
                                );
                              }
                              if (
                                !isCompleted &&
                                !row.buyerConfirmed &&
                                ([
                                  "accepted",
                                  "confirmed",
                                  "buyer_confirmed",
                                ].includes(row.status) ||
                                  row.sellerConfirmed)
                              ) {
                                return (
                                  <button
                                    onClick={() => handleOpenConfirmModal(row)}
                                    title="Approve"
                                    className={`flex h-10 px-3.5 gap-4 items-center justify-center rounded-[8px] transition-colors bg-[#B9FFCC] text-[#16653F] text-[14px] font-[600]`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="13"
                                      viewBox="0 0 18 13"
                                      fill="none"
                                    >
                                      <path
                                        d="M17 1L6 12L1 7"
                                        stroke="#1E1E1E"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    Confirm
                                  </button>
                                );
                              }
                              return null;
                            })()}
                            {/* <button
                              title="Reject"
                              className={`h-10 border border-[#1E1E1E] w-10 flex items-center border-opacity-50 justify-center rounded-[8px] transition-colors`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="20"
                                className="opacity-50"
                                viewBox="0 0 22 20"
                                fill="none"
                              >
                                <path
                                  d="M1 15V14.8498C1 14.5333 1 14.3751 1.02421 14.2209C1.0457 14.084 1.08136 13.9497 1.13061 13.8202C1.18609 13.6743 1.2646 13.5369 1.42162 13.2622L5 7M1 15C1 17.2091 2.79086 19 5 19C7.20914 19 9 17.2091 9 15M1 15V14.8C1 14.52 1 14.38 1.0545 14.273C1.10243 14.1789 1.17892 14.1024 1.273 14.0545C1.37996 14 1.51997 14 1.8 14H8.2C8.48003 14 8.62004 14 8.727 14.0545C8.82108 14.1024 8.89757 14.1789 8.9455 14.273C9 14.38 9 14.52 9 14.8V15M5 7L8.57838 13.2622C8.7354 13.5369 8.81391 13.6743 8.86939 13.8202C8.91864 13.9497 8.9543 14.084 8.97579 14.2209C9 14.3751 9 14.5333 9 14.8498V15M5 7L17 5M13 13V12.8498C13 12.5333 13 12.3751 13.0242 12.2209C13.0457 12.084 13.0814 11.9497 13.1306 11.8202C13.1861 11.6743 13.2646 11.5369 13.4216 11.2622L17 5M13 13C13 15.2091 14.7909 17 17 17C19.2091 17 21 15.2091 21 13M13 13V12.8C13 12.52 13 12.38 13.0545 12.273C13.1024 12.1789 13.1789 12.1024 13.273 12.0545C13.38 12 13.52 12 13.8 12H20.2C20.48 12 20.62 12 20.727 12.0545C20.8211 12.1024 20.8976 12.1789 20.9455 12.273C21 12.38 21 12.52 21 12.8V13M17 5L20.5784 11.2622C20.7354 11.5369 20.8139 11.6743 20.8694 11.8202C20.9186 11.9497 20.9543 12.084 20.9758 12.2209C21 12.3751 21 12.5333 21 12.8498V13M11 1V6"
                                  stroke="#1E1E1E"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button> */}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* Confirmation Modal */}
      <ConfirmTransactionModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSeller}
        transactionId={selectedTransaction?.id}
        itemName={selectedTransaction?.listing?.title}
        isLoading={confirmingId === selectedTransaction?.id}
        errorText={modalError}
      />
    </LayoutBuyerSeller>
  );
};

export default BuyerTransactions;
